// /memberships — Membership Plans management (PAGES.md §3.94c)
//
// Gold/royal gradient hero, 4 KPI tiles (total members, active members,
// revenue, renewal rate), filter bar, DataTable with all spec columns.
// OWNER (50)+ viewing and create per §3.94c.

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  PageContainer,
  ErrorBoundary,
  Input,
  Select,
  Button,
  DataTable,
  Skeleton,
  EmptyState,
  Badge,
  useToast,
} from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import { formatPaise } from '@/utils/currency';
import { useAuthStore } from '@/stores/use-auth-store';
import { ROLE_LEVELS } from '@/types/user';
import {
  useMembershipPlanList,
  useMembershipAggregateStats,
  MembershipTierPill,
  MembershipStatusPill,
  formatDuration,
} from '@/features/memberships';
import type { MembershipPlan, MembershipPlanStatus, MembershipTier } from '@/features/memberships';
import { ReportKpiRow, KpiIcons, downloadCsv } from '@/features/reports';
import { getCurrencyDecimals, getSubunitMultiplier } from '@bbpos/utils';
import { useTenantCurrency, getCurrencySymbol } from '@/hooks/use-tenant-currency';
import type { CurrencyCode } from '@bbpos/types';

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
];

const TIER_OPTIONS = [
  { value: '', label: 'All Tiers' },
  { value: 'SILVER', label: 'Silver' },
  { value: 'GOLD', label: 'Gold' },
  { value: 'PLATINUM', label: 'Platinum' },
  { value: 'CUSTOM', label: 'Custom' },
];

type Row = MembershipPlan;

function buildColumns(currency: CurrencyCode): ColumnDef<Row>[] {
  return [
  {
    key: 'name',
    header: 'Tier Name',
    render: (_v, row) => (
      <Link
        to={`/memberships/${row.id}`}
        className="group flex items-center gap-3 -my-1 rounded-lg px-2 py-1 transition-colors hover:bg-amber-50 dark:hover:bg-amber-900/20"
      >
        <MembershipTierPill tier={row.tier_level} size="sm" />
        <div>
          <p className="text-sm font-bold text-neutral-900 dark:text-neutral-100 group-hover:text-amber-700 dark:group-hover:text-amber-300">
            {row.name}
          </p>
          {row.description ? (
            <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400 line-clamp-1 max-w-[28rem]">
              {row.description}
            </p>
          ) : null}
        </div>
      </Link>
    ),
  },
  {
    key: 'fee',
    header: `Fee (${getCurrencySymbol(currency)})`,
    numeric: true,
    render: (_v, row) => (
      <span className="text-sm font-bold text-neutral-900 dark:text-neutral-100 tabular-nums">
        {formatPaise(row.fee, currency)}
      </span>
    ),
  },
  {
    key: 'duration_days',
    header: 'Duration',
    numeric: true,
    render: (_v, row) => (
      <span className="text-sm text-neutral-700 dark:text-neutral-200 tabular-nums">
        {formatDuration(row.duration_days).short}
      </span>
    ),
  },
  {
    key: 'discount_percentage',
    header: 'Discount %',
    numeric: true,
    render: (_v, row) => (
      <span className="text-sm text-neutral-700 dark:text-neutral-200 tabular-nums">
        {row.discount_percentage > 0 ? `${row.discount_percentage}%` : '—'}
      </span>
    ),
  },
  {
    key: 'points_multiplier',
    header: 'Points ×',
    numeric: true,
    render: (_v, row) => (
      <span className="text-sm text-neutral-700 dark:text-neutral-200 tabular-nums">
        {row.points_multiplier > 1 ? `${row.points_multiplier}×` : '1×'}
      </span>
    ),
  },
  {
    key: 'priority_service',
    header: 'Priority',
    render: (_v, row) =>
      row.priority_service ? (
        <Badge variant="success" size="sm">Yes</Badge>
      ) : (
        <span className="text-xs text-neutral-400 dark:text-neutral-500">—</span>
      ),
  },
  {
    key: 'auto_renewal',
    header: 'Auto-Renew',
    render: (_v, row) =>
      row.auto_renewal ? (
        <Badge variant="success" size="sm">On</Badge>
      ) : (
        <span className="text-xs text-neutral-400 dark:text-neutral-500">Off</span>
      ),
  },
  {
    key: 'is_active',
    header: 'Status',
    render: (_v, row) => <MembershipStatusPill isActive={row.is_active} />,
  },
  ];
}

function MembershipPlansListContent() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const currency = useTenantCurrency();
  const columns = useMemo(() => buildColumns(currency), [currency]);
  const [searchParams, setSearchParams] = useSearchParams();
  const searchRef = useRef<HTMLInputElement | null>(null);

  const role = useAuthStore((s) => s.profile?.role ?? '');
  const roleLevel = ROLE_LEVELS[role] ?? 0;
  const canCreate = roleLevel >= ROLE_LEVELS.OWNER;

  const page = Number(searchParams.get('page') ?? '1');
  const search = searchParams.get('search') ?? '';
  const status = (searchParams.get('status') ?? '') as MembershipPlanStatus | '';
  const tier = (searchParams.get('tier') ?? '') as MembershipTier | '';

  const { data, isLoading, isFetching, error } = useMembershipPlanList({ page, search, status, tier });
  const statsQ = useMembershipAggregateStats();

  const rows = useMemo<Row[]>(() => data?.data ?? [], [data]);
  const totalCount = data?.meta.total ?? 0;

  function setParam(key: string, value: string) {
    const params = new URLSearchParams(searchParams);
    if (value) params.set(key, value);
    else params.delete(key);
    if (key !== 'page') params.set('page', '1');
    setSearchParams(params);
  }

  // Keyboard shortcuts: / focus search, n → new plan (OWNER only)
  const canCreateRef = useRef(canCreate);
  canCreateRef.current = canCreate;
  useEffect(() => {
    function handler(e: KeyboardEvent) {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === '/') {
        e.preventDefault();
        searchRef.current?.focus();
      } else if (e.key === 'n' && canCreateRef.current) {
        e.preventDefault();
        navigate('/memberships/new');
      }
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [navigate]);

  const handleExport = useCallback(() => {
    if (rows.length === 0) {
      toast({
        variant: 'warning',
        title: 'Nothing to export',
        description: 'The current view has no membership plans.',
      });
      return;
    }
    downloadCsv<Row>(
      `memberships-${new Date().toISOString().slice(0, 10)}.csv`,
      [
        { header: 'Name', get: (r) => r.name },
        { header: 'Tier', get: (r) => r.tier_level },
        {
          header: `Fee (${getCurrencySymbol(currency)})`,
          get: (r) => (r.fee / getSubunitMultiplier(currency)).toFixed(getCurrencyDecimals(currency)),
        },
        { header: 'Duration (days)', get: (r) => r.duration_days },
        { header: 'Discount %', get: (r) => r.discount_percentage },
        { header: 'Points Multiplier', get: (r) => r.points_multiplier },
        { header: 'Priority Service', get: (r) => (r.priority_service ? 'Yes' : 'No') },
        { header: 'Auto Renewal', get: (r) => (r.auto_renewal ? 'Yes' : 'No') },
        { header: 'Status', get: (r) => (r.is_active ? 'ACTIVE' : 'INACTIVE') },
        { header: 'Created', get: (r) => r.created_at },
      ],
      rows,
    );
  }, [rows, toast, currency]);

  const stats = statsQ.data;

  return (
    <div className="animate-fade-in">
      {/* Hero — gold/royal gradient, distinct from Loyalty amber/rose and Voucher brand/indigo */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-amber-500 via-yellow-500 to-orange-500 p-6 sm:p-7 text-white mb-6 shadow-[0_25px_70px_-25px_rgba(245,158,11,0.7)]">
        <div className="pointer-events-none absolute -top-24 -right-16 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-16 -left-10 h-48 w-48 rounded-full bg-black/15 blur-3xl" />
        <div className="relative flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-[10px] font-bold uppercase tracking-wider ring-1 ring-white/25 backdrop-blur-sm">
              Subscription Tiers
            </span>
            <h1 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-white">Membership Plans</h1>
            <p className="mt-1.5 text-sm text-white/90 max-w-xl">
              Sell paid tiers that turn regulars into VIPs. Set pricing, benefits and auto-renewal.
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
              <span className="rounded-full bg-white/15 backdrop-blur-sm px-3 py-1 font-semibold tabular-nums">
                {totalCount.toLocaleString('en-IN')} total plans
              </span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="secondary"
              onClick={handleExport}
              className="bg-white/15 hover:bg-white/25 text-white border-white/30 backdrop-blur-sm"
            >
              Export CSV
            </Button>
            {canCreate ? (
              <Button
                variant="primary"
                onClick={() => navigate('/memberships/new')}
                className="!bg-white !text-amber-700 hover:!bg-white/90"
              >
                + Create Plan
              </Button>
            ) : null}
          </div>
        </div>
      </div>

      {/* KPI row — §3.94c: total members, active members, revenue, renewal rate */}
      {statsQ.isLoading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5 mb-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} variant="rect" height={120} className="rounded-xl" />
          ))}
        </div>
      ) : (
        <ReportKpiRow
          tiles={[
            {
              id: 'total-members',
              title: 'Total Members',
              value: (stats?.total_members ?? 0).toLocaleString('en-IN'),
              subLabel: 'Across all plans',
              tone: 'brand',
              icon: KpiIcons.users,
            },
            {
              id: 'active-members',
              title: 'Active Members',
              value: (stats?.active_members ?? 0).toLocaleString('en-IN'),
              subLabel: 'Currently enrolled',
              tone: 'success',
              icon: KpiIcons.users,
            },
            {
              id: 'revenue',
              title: 'Revenue',
              value: formatPaise(stats?.total_revenue ?? 0, currency),
              subLabel: 'From membership fees',
              tone: 'indigo',
              icon: KpiIcons.rupee,
            },
            {
              id: 'renewal-rate',
              title: 'Renewal Rate',
              value: stats?.renewal_rate_pct != null ? `${stats.renewal_rate_pct.toFixed(1)}%` : '—',
              subLabel: 'Members who renewed',
              tone: 'violet',
              icon: KpiIcons.receipt,
            },
          ]}
        />
      )}

      {/* Filter bar */}
      <div className="rounded-xl bg-white dark:bg-neutral-800 shadow-md p-4 sm:p-5 mb-6">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <div className="flex-1 min-w-0">
            <Input
              id="membership-search"
              variant="search"
              placeholder="Search by plan name (press / to focus)"
              value={search}
              onChange={(v) => setParam('search', v)}
              aria-label="Search membership plans"
            />
          </div>
          <div className="w-full lg:w-44">
            <Select
              options={TIER_OPTIONS}
              value={tier}
              onChange={(v) => setParam('tier', typeof v === 'string' ? v : '')}
            />
          </div>
          <div className="w-full lg:w-44">
            <Select
              options={STATUS_OPTIONS}
              value={status}
              onChange={(v) => setParam('status', typeof v === 'string' ? v : '')}
            />
          </div>
        </div>
      </div>

      {/* Data */}
      {error ? (
        <div className="rounded-xl bg-danger-50 dark:bg-danger-900/20 shadow-md p-4 text-sm text-danger-700 dark:text-danger-300">
          Failed to load membership plans. Please refresh.
        </div>
      ) : isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} variant="rect" height={72} className="rounded-lg" />
          ))}
        </div>
      ) : rows.length === 0 && !isFetching ? (
        <div className="rounded-2xl bg-white dark:bg-neutral-800 shadow-md p-10 animate-fade-in">
          <EmptyState
            variant="page"
            title="No membership plans"
            description="Create membership tiers to reward your loyal customers with exclusive benefits"
            action={
              canCreate
                ? { label: '+ Create Plan', onClick: () => navigate('/memberships/new') }
                : undefined
            }
            icon={
              <svg
                className="h-20 w-20 text-amber-300 dark:text-amber-700"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.3}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M5 3l3.5 4.5L12 3l3.5 4.5L19 3v13a2 2 0 01-2 2H7a2 2 0 01-2-2V3z"
                />
              </svg>
            }
          />
        </div>
      ) : (
        <section className="rounded-2xl bg-white dark:bg-neutral-800 shadow-md overflow-hidden animate-slide-up">
          <DataTable<Row>
            columns={columns}
            data={rows}
            loading={isFetching}
            totalCount={data?.meta.total}
            currentPage={page}
            pageSize={20}
            onPageChange={(p) => setParam('page', String(p))}
            stickyHeader
          />
        </section>
      )}
    </div>
  );
}

export function MembershipPlansListPage() {
  return (
    <PageContainer
      title="Membership Plans"
      breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Memberships' }]}
    >
      <ErrorBoundary resetKey="memberships-list">
        <MembershipPlansListContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
